import PropTypes from 'prop-types';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';

// ==============================|| STRUCTURED RESULT ||============================== //
//
// Renders the `structured` payload from the `answer` SSE event as neatly aligned
// lists (not real tables). The plain-text answer is shown separately above this.

const Row = ({ label, value }) => (
  <Stack direction="row" spacing={1} sx={{ py: 0.25 }}>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        minWidth: 96,
        flexShrink: 0
      }}
    >
      {label}
    </Typography>
    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </Stack>
);

Row.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
};

const DeviceCard = ({ device }) => (
  <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        mb: 0.5
      }}
    >
      <Typography variant="subtitle2">{device.serialNumber}</Typography>
      <Chip
        size="small"
        label={device.availability === 1 ? 'ONLINE' : 'OFFLINE'}
        color={device.availability === 1 ? 'success' : 'default'}
        variant={device.availability === 1 ? 'filled' : 'outlined'}
      />
    </Stack>
    {device.modelNumber && <Row label="Model" value={device.modelNumber} />}
    {device.macAddress && <Row label="MAC" value={device.macAddress} />}
  </Box>
);

DeviceCard.propTypes = {
  device: PropTypes.object
};

const UpdateCard = ({ update }) => (
  <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        mb: 0.5
      }}
    >
      <Typography variant="subtitle2">{update.version}</Typography>
      {update.access && <Chip size="small" label={update.access} variant="outlined" />}
    </Stack>
    {update.description && <Row label="Notes" value={update.description} />}
    {update.releaseDate && <Row label="Released" value={new Date(update.releaseDate).toLocaleDateString()} />}
    {update.fileName && <Row label="File" value={update.fileName} />}
    {update.fileSizeBytes != null && <Row label="Size" value={formatBytes(update.fileSizeBytes)} />}
  </Box>
);

UpdateCard.propTypes = {
  update: PropTypes.object
};

const Section = ({ title, children }) => (
  <Box>
    <Typography
      variant="overline"
      sx={{
        color: 'text.secondary'
      }}
    >
      {title}
    </Typography>
    <Stack spacing={1} sx={{ mt: 0.5 }}>
      {children}
    </Stack>
  </Box>
);

Section.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node
};

const StructuredResult = ({ structured }) => {
  if (!structured) return null;
  const devices = structured.devices || [];
  const updates = structured.updates || [];
  const errors = structured.errors || [];

  if (!devices.length && !updates.length && !errors.length) return null;

  return (
    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
      <Divider />
      {devices.length > 0 && (
        <Section title={`Devices (${devices.length})`}>
          {devices.map((d, i) => (
            <DeviceCard key={d.serialNumber || i} device={d} />
          ))}
        </Section>
      )}
      {updates.length > 0 && (
        <Section title={`Firmware updates (${updates.length})`}>
          {updates.map((u, i) => (
            <UpdateCard key={u.version || i} update={u} />
          ))}
        </Section>
      )}
      {errors.length > 0 && (
        <Section title="Issues">
          {errors.map((e, i) => (
            <Typography key={i} variant="caption" color="error">
              {typeof e === 'string' ? e : e.errorMessage || e.code}
            </Typography>
          ))}
        </Section>
      )}
    </Stack>
  );
};

StructuredResult.propTypes = {
  structured: PropTypes.object
};

export default StructuredResult;
